import { useCallback, useContext, useMemo } from 'react'
import { Dimensions } from 'types'
import { UgeArtboardContext } from '../../contexts/artboard'
import { UgeCropContext } from '../../contexts/crop'
import { cropAspectRatio } from '../../types/crop'
import { Label } from '../style'
import { Ext, NumberInput, RatioLock } from './style'

type Props = {
    className?: string
}

const CropEditorResize = ({ className }: Props) => {
    const { crop, cropType, setCrop } = useContext(UgeCropContext)
    const board = useContext(UgeArtboardContext)

    const aspectRatio = useMemo(() => cropAspectRatio(cropType, board.aspectRatio), [board.aspectRatio, cropType])
    const cropHeight = useMemo(() => crop?.height || board.height, [board.height, crop?.height])
    const cropWidth = useMemo(() => crop?.width || board.width, [board.width, crop?.width])

    const updateCrop = useCallback(
        (dimensions: Dimensions) => {
            const { height = board.height, width = board.width, x = 0, y = 0 } = crop || {}

            setCrop({
                ...dimensions,
                x: x + Math.round((width - dimensions.width) / 2),
                y: y + Math.round((height - dimensions.height) / 2),
            })
        },
        [board, crop]
    )

    const onHeightChange = useCallback(
        (value: number) => {
            updateCrop({
                height: value,
                width: aspectRatio ? Math.round(value * aspectRatio) : cropWidth,
            })
        },
        [aspectRatio, cropWidth]
    )

    const onWidthChange = useCallback(
        (value: number) => {
            updateCrop({
                height: aspectRatio ? Math.round(value / aspectRatio) : cropHeight,
                width: value,
            })
        },
        [aspectRatio, cropHeight]
    )

    return (
        <div className={className}>
            <Label>Resize (px)</Label>
            <NumberInput
                extension={<Ext>W</Ext>}
                max={board.width}
                min={0}
                value={cropWidth}
                onChange={onWidthChange}
            />
            <NumberInput
                extension={<Ext>H</Ext>}
                max={board.height}
                min={0}
                value={cropHeight}
                onChange={onHeightChange}
            />
            {!!aspectRatio && <RatioLock className="ss-link" />}
        </div>
    )
}

export default CropEditorResize
